import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box, Typography } from "@mui/material";
import {
  MyTimesheetCalendarToolbar,
  MyTimesheetMyTimesheetTableContainer,
  MyTimesheetMyTimesheetTableDayHeader,
} from "my-timesheet/components";
import { OcIconForState } from "oc/components";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

type Timesheet = {
  date: string;
  timesheets: any[];
  dailySum: number | string;
};

type Week = {
  weekNumber: string;
  weeklySum: number | string;
  timesheets: Timesheet[];
};

type MyTimesheetMyTimesheetTableProps = {
  data: Week[];
  onShowRequest: (request: any) => void;
  currentDate: Date;
  onChangeCurrentDate: (date: Date) => void;
  period: "month" | "week" | "day";
  onChangePeriod: (period: string) => void;
  showDateSelector: boolean;
  showOnlyRequests: boolean;
  loading: boolean;
  error: any;
};

export const MyTimesheetMyTimesheetTable: React.FC<
  MyTimesheetMyTimesheetTableProps
> = ({
  data,
  onShowRequest,
  currentDate,
  onChangeCurrentDate,
  period,
  onChangePeriod,
  showDateSelector,
  showOnlyRequests,
  loading,
  error,
}) => {
    const { t } = useTranslation();
    const isMobile = useOcIsMobile();

    const currentWeek = moment().week().toString();

    const handleNavigate = (direction: string) => {
      switch (direction) {
        case "PREV":
          onChangeCurrentDate(
            moment(currentDate)
              .subtract(1, period as moment.unitOfTime.DurationConstructor)
              .toDate(),
          );
          break;
        case "NEXT":
          onChangeCurrentDate(
            moment(currentDate)
              .add(1, period as moment.unitOfTime.DurationConstructor)
              .toDate(),
          );
          break;
        case "TODAY":
          onChangeCurrentDate(new Date());
          break;
        default:
          break;
      }
    };

    const handleViewChange = (view: string) => {
      onChangePeriod(view);
    };

    return (
      <>
        <Box>
          {showDateSelector && (
            <MyTimesheetCalendarToolbar
              view={period}
              onView={(prop) => handleViewChange(prop)}
              onNavigate={(prop) => handleNavigate(prop)}
              date={currentDate}
            />
          )}
        </Box>
        {!data?.length ? (
          <OcIconForState
            data={data}
            loading={loading}
            isMobile={isMobile}
            error={error}
            module="MyTimesheet"
          />
        ) : (
          data?.map((week) => {
            const { weekNumber, weeklySum, timesheets } = week;

            const weekString = () => {
              if (weekNumber === currentWeek) {
                return t("MY_TIMESHEET_THIS_WEEK");
              } else if (Number.parseInt(weekNumber) === Number(currentWeek) - 1) {
                return t("MY_TIMESHEET_LAST_WEEK");
              } else {
                let startOfWeek = moment()
                  .week(Number(weekNumber))
                  .startOf("week")
                  .format("MMMM DD.");
                let endOfWeek = moment()
                  .week(Number(weekNumber))
                  .endOf("week")
                  .format("MMMM DD.");
                return `${startOfWeek} - ${endOfWeek}`;
              }
            };

            return (
              <Box key={weekNumber}>
                {!showOnlyRequests && (
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    marginBottom="32px"
                  >
                    <Typography variant="h5" color="primary.main">
                      {weekString()}
                    </Typography>
                    <Typography variant="body1">
                      {t("MY_TIMESHEET_WEEK_TOTAL")}:{" "}
                      <span style={{ fontWeight: 700 }}>{weeklySum}</span>
                    </Typography>
                  </Box>
                )}
                {timesheets.map((timesheet, index) => {
                  const { date, timesheets: dayRows, dailySum } = timesheet;
                  const isLastItem = index === timesheets.length - 1;
                  const tableId = `MyTimeTable_${weekNumber}_${index}`;
                  return (
                    <Box key={`timesheet-${weekNumber}-${index}`}>
                      <MyTimesheetMyTimesheetTableDayHeader
                        date={date}
                        dailySum={dailySum}
                      />
                      <MyTimesheetMyTimesheetTableContainer
                        isMobile={isMobile}
                        data={dayRows}
                        onShowRequest={onShowRequest}
                        tableId={tableId}
                      />
                      {!isLastItem && <Box height={"32px"} />}
                    </Box>
                  );
                })}
                <Box height={"32px"} />
              </Box>
            );
          })
        )}
      </>
    );
  };

export default MyTimesheetMyTimesheetTable;
